import React, { createContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { saveAnswer as saveAnswerApi, reportViolation, submitAssessment as submitAssessmentApi, sessionSave, sessionSubmit, sessionPosition } from '../api/publicAssessApi';

const AssessmentSessionContext = createContext();

export const shouldRestoreSession = (savedSessionStr, currentToken) => {
  if (!savedSessionStr || !currentToken) return false;
  try {
    const session = typeof savedSessionStr === 'string' ? JSON.parse(savedSessionStr) : savedSessionStr;
    return Boolean(session && session.token === currentToken && session.status === 'active');
  } catch (e) {
    return false;
  }
};

const initialState = {
  token: null,
  sessionToken: null,
  driveInfo: null,
  questions: [],
  answers: {},
  currentQuestionIndex: 0,
  timeRemaining: null,
  endsAt: null,
  serverOffset: 0,
  violations: 0,
  isSubmitted: false,
  status: 'idle',
  error: null,
};

const sessionReducer = (state, action) => {
  console.log('Reducer Action', action.type);
  console.log('Reducer Payload', action.payload);

  let newState;

  switch (action.type) {
    case 'INIT_SESSION':
      console.log('REDUCER INIT_SESSION', action.payload);
      newState = {
        ...state,
        token: action.payload.token,
        sessionToken: action.payload.sessionToken || null,
        driveInfo: action.payload.driveInfo,
        questions: action.payload.questions,
        // Resume from the question the student was last on (server-provided), else question 1
        currentQuestionIndex: Number.isInteger(action.payload.currentQuestionIndex) ? action.payload.currentQuestionIndex : 0,
        // Keep violations already recorded on the server (e.g. after logging in from another device)
        violations: Number.isInteger(action.payload.violationCount) ? action.payload.violationCount : state.violations,
        // Load any saved answers provided by the server
        answers: action.payload.answers || {},
        status: 'ready',
      };
      break;
    case 'START_SESSION':
      console.log('REDUCER START_SESSION', action.payload);
      newState = {
        ...state,
        status: 'active',
        timeRemaining: action.payload.timeRemaining,
        endsAt: action.payload.endsAt,
        serverOffset: action.payload.serverOffset,
      };
      break;
    case 'SET_ANSWER':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.questionId]: action.payload.optionId,
        },
      };
    case 'SET_CURRENT_QUESTION':
      return {
        ...state,
        currentQuestionIndex: action.payload,
      };
    case 'TICK_TIMER':
      if (state.endsAt) {
        const currentServerTime = Date.now() + state.serverOffset;
        const remaining = Math.max(0, Math.floor((state.endsAt - currentServerTime) / 1000));
        return {
          ...state,
          timeRemaining: remaining,
        };
      }
      return {
        ...state,
        timeRemaining: Math.max(0, state.timeRemaining - 1),
      };
    case 'ADD_VIOLATION':
      return {
        ...state,
        violations: state.violations + 1,
      };
    case 'SUBMIT_ASSESSMENT':
      return {
        ...state,
        isSubmitted: true,
        status: 'submitted',
      };
    // RESTORE_SESSION removed as we rely on database state instead
    case 'SET_ERROR':
      newState = {
        ...state,
        error: action.payload,
      };
      break;
    default:
      newState = state;
      break;
  }

  console.log('Reducer New State', newState);
  return newState;
};

export const AssessmentSessionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(sessionReducer, initialState);
  
  // Removed sessionStorage persistence and restoration useEffects

  
  const initSession = useCallback((data) => {
    console.log('INIT_SESSION DISPATCH', data);
    dispatch({ type: 'INIT_SESSION', payload: data });
    const qs = Array.isArray(data?.questions) ? data.questions : [];
    if (qs.length > 0) {
      // Calculate server offset if serverTime is provided
      const serverTime = data?.serverTime ? new Date(data.serverTime).getTime() : Date.now();
      const serverOffset = serverTime - Date.now();
      
      const endsAt = data?.endsAt ? new Date(data.endsAt).getTime() : null;

      const remainingTime = typeof data?.remainingTime === 'number'
        ? data.remainingTime
        : (data?.driveInfo?.durationMinutes ? Number(data.driveInfo.durationMinutes) * 60 : null);

      if (remainingTime !== null) {
        console.log('START SESSION', { remainingTime, endsAt, serverOffset });
        dispatch({ type: 'START_SESSION', payload: { timeRemaining: remainingTime, endsAt, serverOffset } });
      }
    }
  }, []);

  // Heartbeat: tells the server the student is still here (and which question they are on).
  // If the desktop dies, the server uses the silence to pause the exam clock on re-login.
  const currentIndexRef = useRef(0);
  currentIndexRef.current = state.currentQuestionIndex;
  useEffect(() => {
    if (state.status !== 'active' || state.isSubmitted || !state.token || !state.sessionToken) return undefined;
    const beat = () => {
      sessionPosition({
        token: state.token,
        sessionToken: state.sessionToken,
        currentQuestionIndex: currentIndexRef.current
      }).catch(() => {});
    };
    const id = setInterval(beat, 15000);
    return () => clearInterval(id);
  }, [state.status, state.isSubmitted, state.token, state.sessionToken]);

  const startSession = useCallback((timeRemaining) => dispatch({ type: 'START_SESSION', payload: { timeRemaining } }), []);

  const setAnswer = useCallback(async (questionId, optionId) => {
    dispatch({ type: 'SET_ANSWER', payload: { questionId, optionId } });
    if (state.token) {
      try {
        if (state.sessionToken) {
          await sessionSave({ token: state.token, sessionToken: state.sessionToken, questionId, selectedOptionId: optionId });
        } else {
          await saveAnswerApi({ token: state.token, questionId, optionId });
        }
      } catch (err) {
        console.error("Failed to auto-save:", err);
      }
    }
  }, [state.token, state.sessionToken]);

  const setCurrentQuestion = useCallback((index) => {
    dispatch({ type: 'SET_CURRENT_QUESTION', payload: index });
    // Remember the position on the server so a re-login resumes here (fire and forget)
    if (state.token && state.sessionToken) {
      sessionPosition({ token: state.token, sessionToken: state.sessionToken, currentQuestionIndex: index })
        .catch((err) => console.error('Failed to save question position:', err));
    }
  }, [state.token, state.sessionToken]);
  
  const tickTimer = useCallback(() => dispatch({ type: 'TICK_TIMER' }), []);

  const addViolation = useCallback(async (type = 'TAB_SWITCH') => {
    dispatch({ type: 'ADD_VIOLATION' });
    if (state.token) {
      try {
        await reportViolation({ token: state.token, type });
      } catch (err) {
        console.error("Failed to report violation:", err);
      }
    }
  }, [state.token]);

  const submitAssessment = useCallback(async () => {
    if (state.token) {
      try {
        if (state.sessionToken) {
          await sessionSubmit({ token: state.token, sessionToken: state.sessionToken });
        } else {
          await submitAssessmentApi({ token: state.token });
        }
      } catch (err) {
        console.error("Submit failed:", err);
      }
    }
    dispatch({ type: 'SUBMIT_ASSESSMENT' });
  }, [state.token, state.sessionToken]);

  return (
    <AssessmentSessionContext.Provider value={{
      ...state,
      initSession,
      startSession,
      setAnswer,
      setCurrentQuestion,
      tickTimer,
      addViolation,
      submitAssessment
    }}>
      {children}
    </AssessmentSessionContext.Provider>
  );
};

export default AssessmentSessionContext;
